/**
 * CatchMeUpPanel — slide-out "since you were last here" digest.
 *
 * Replaces the chat-event flow for the dashboard "Catch me up" card.
 * Pulls real data from existing endpoints in parallel and renders it
 * as a structured, scannable panel:
 *
 *   • Unread Messages
 *   • Pages Awaiting My Action
 *   • Overdue Items
 *   • Audit Findings & CAPAs
 *   • Recent Notifications
 *
 * Each section is a card with icon, title, count, and an expandable
 * list. Items link to the relevant page so a user can act in one click.
 *
 * Visual style mirrors AIAdvisorPanel (right-side drawer, sectioned
 * cards, severity colours) so users get a familiar, scannable layout
 * instead of a conversational reply.
 *
 * Data sources (existing endpoints — no new backend work):
 *   GET /api/messaging?excludeType=feedback     → unread filter applied client-side
 *   GET /api/notification?limit=20              → recent notifications
 *   GET /api/compliance/execution/company/pages → company pages
 *   GET /api/compliance/capa?limit=20           → CAPAs
 *   GET /api/audit/findings (optional, persona-scoped)
 *
 * Each section degrades gracefully — if an endpoint 401/403/404s, the
 * card shows "Not available for your role" instead of erroring.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  X, RefreshCw, Mail, FileText, AlertTriangle, ShieldCheck,
  Bell, Clock, ChevronRight, CheckCircle2, Loader2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface CatchMeUpPanelProps {
  isOpen: boolean;
  onClose: () => void;
  lastLoginAt: string | null;
  userDomain: 'admin' | 'partner' | 'company' | 'compliance' | 'audit' | 'emissions';
}

interface DigestItem {
  id: number | string;
  title: string;
  subtitle?: string;
  meta?: string;
  href?: string;
  severity?: 'critical' | 'high' | 'medium' | 'low' | 'info';
}

interface DigestSection {
  key: string;
  icon: React.ReactNode;
  title: string;
  loading: boolean;
  error?: string | null;
  items: DigestItem[];
  emptyText: string;
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
  'Content-Type': 'application/json',
});

const safeJson = async (res: Response): Promise<any> => {
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
};

const formatRelative = (iso: string | null): string => {
  if (!iso) return 'recently';
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const severityClasses = (sev?: string) => {
  switch (sev) {
    case 'critical': return 'bg-red-50 border-red-200 text-red-700';
    case 'high':     return 'bg-orange-50 border-orange-200 text-orange-700';
    case 'medium':   return 'bg-amber-50 border-amber-200 text-amber-700';
    case 'low':      return 'bg-blue-50 border-blue-200 text-blue-700';
    default:         return 'bg-slate-50 border-slate-200 text-slate-700';
  }
};

const messagesPath = (domain: string) => {
  switch (domain) {
    case 'admin':      return '/admin/messages';
    case 'company':    return '/company/messages';
    case 'audit':      return '/audit/messages';
    case 'compliance': return '/compliance/messages';
    default:           return '/admin/messages';
  }
};

const CatchMeUpPanel: React.FC<CatchMeUpPanelProps> = ({ isOpen, onClose, lastLoginAt, userDomain }) => {
  const navigate = useNavigate();
  const [sections, setSections] = useState<Record<string, DigestSection>>({
    messages:      { key: 'messages',      icon: <Mail size={16} />,         title: 'Unread Messages',         loading: true, items: [], emptyText: 'You\'re caught up — no unread messages.' },
    pages:         { key: 'pages',         icon: <FileText size={16} />,     title: 'Pages Awaiting Action',   loading: true, items: [], emptyText: 'No pages need your attention right now.' },
    overdue:       { key: 'overdue',       icon: <Clock size={16} />,        title: 'Overdue Items',           loading: true, items: [], emptyText: 'Nothing overdue. Nice work staying on top of things.' },
    capas:         { key: 'capas',         icon: <ShieldCheck size={16} />,  title: 'Open CAPAs & Findings',   loading: true, items: [], emptyText: 'No open CAPAs or findings assigned to you.' },
    notifications: { key: 'notifications', icon: <Bell size={16} />,         title: 'Recent Notifications',    loading: true, items: [], emptyText: 'No new notifications since your last login.' },
  });

  const updateSection = (key: string, patch: Partial<DigestSection>) => {
    setSections(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const sinceCutoff = lastLoginAt ? new Date(lastLoginAt) : new Date(Date.now() - 24 * 3600 * 1000);

  // ─── Fetchers ────────────────────────────────────────────────────────
  const fetchMessages = useCallback(async () => {
    updateSection('messages', { loading: true, error: null });
    try {
      const res = await fetch('/api/messaging?excludeType=feedback', { headers: authHeaders() });
      const data = await safeJson(res);
      const all: any[] = Array.isArray(data?.messages) ? data.messages : Array.isArray(data?.data) ? data.data : [];
      const unread = all.filter(m => m.isRead === false || m.is_read === 0 || m.is_read === false).slice(0, 10);
      updateSection('messages', {
        loading: false,
        items: unread.map(m => ({
          id: m.id,
          title: m.subject || m.title || '(no subject)',
          subtitle: m.from?.name || m.sender_name || m.from?.email || 'Sender unknown',
          meta: formatRelative(m.created_at || m.createdAt || null),
          href: `${messagesPath(userDomain)}?id=${m.id}`,
          severity: m.priority === 'urgent' || m.priority === 'high' ? 'high' : 'info',
        })),
      });
    } catch (e: any) {
      updateSection('messages', { loading: false, error: 'Could not load messages', items: [] });
    }
  }, [userDomain]);

  const fetchPages = useCallback(async () => {
    updateSection('pages', { loading: true, error: null });
    try {
      // Generic: fetch company pages and filter to ones in actionable status.
      const res = await fetch('/api/compliance/execution/company/pages', { headers: authHeaders() });
      const data = await safeJson(res);
      const all: any[] = Array.isArray(data?.data) ? data.data : Array.isArray(data?.pages) ? data.pages : [];
      const ACTIONABLE = new Set(['submitted', 'pending', 'in_progress']);
      const items = all
        .filter(p => ACTIONABLE.has(p.status))
        .slice(0, 10)
        .map(p => ({
          id: p.id,
          title: p.title || `Page #${p.id}`,
          subtitle: p.framework_name ? `${p.framework_name}${p.section_name ? ' · ' + p.section_name : ''}` : 'Compliance page',
          meta: p.status,
          href: `/compliance/execution/page/${p.id}`,
          severity: p.status === 'submitted' ? 'medium' : 'low',
        } as DigestItem));
      updateSection('pages', { loading: false, items });
    } catch (e: any) {
      updateSection('pages', { loading: false, error: 'Not available for your role', items: [] });
    }
  }, []);

  const fetchOverdue = useCallback(async () => {
    updateSection('overdue', { loading: true, error: null });
    try {
      const res = await fetch('/api/compliance/execution/company/pages?overdue_only=true', { headers: authHeaders() });
      const data = await safeJson(res);
      const all: any[] = Array.isArray(data?.data) ? data.data : Array.isArray(data?.pages) ? data.pages : [];
      const today = new Date();
      const overdue = all.filter(p => {
        if (!p.due_date) return false;
        return new Date(p.due_date) < today && !['certified', 'approved_for_audit'].includes(p.status);
      }).slice(0, 10);
      updateSection('overdue', {
        loading: false,
        items: overdue.map(p => ({
          id: p.id,
          title: p.title || `Page #${p.id}`,
          subtitle: p.framework_name || 'Compliance page',
          meta: `due ${formatRelative(p.due_date)}`,
          href: `/compliance/execution/page/${p.id}`,
          severity: 'high',
        })),
      });
    } catch (e: any) {
      updateSection('overdue', { loading: false, error: 'Not available for your role', items: [] });
    }
  }, []);

  const fetchCapas = useCallback(async () => {
    updateSection('capas', { loading: true, error: null });
    try {
      const res = await fetch('/api/compliance/capa?limit=20', { headers: authHeaders() });
      const data = await safeJson(res);
      const all: any[] = Array.isArray(data?.data) ? data.data : Array.isArray(data?.capas) ? data.capas : [];
      const open = all.filter(c => c.status !== 'verified_closed' && c.status !== 'closed').slice(0, 10);
      updateSection('capas', {
        loading: false,
        items: open.map(c => ({
          id: c.id,
          title: c.title || c.description || `CAPA #${c.id}`,
          subtitle: c.status || 'open',
          meta: c.due_date ? `due ${formatRelative(c.due_date)}` : undefined,
          href: `/compliance/capa/${c.id}`,
          severity: c.priority === 'high' || c.priority === 'critical' ? 'high' : 'medium',
        })),
      });
    } catch (e: any) {
      updateSection('capas', { loading: false, error: 'Not available for your role', items: [] });
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    updateSection('notifications', { loading: true, error: null });
    try {
      const res = await fetch('/api/notification?limit=20', { headers: authHeaders() });
      const data = await safeJson(res);
      const all: any[] = Array.isArray(data?.data) ? data.data : Array.isArray(data?.notifications) ? data.notifications : [];
      const recent = all
        .filter(n => !lastLoginAt || new Date(n.createdAt || n.created_at || 0) >= sinceCutoff)
        .slice(0, 10);
      updateSection('notifications', {
        loading: false,
        items: recent.map(n => {
          let parsedData: any = n.data;
          if (typeof parsedData === 'string') {
            try { parsedData = JSON.parse(parsedData); } catch { parsedData = null; }
          }
          return {
            id: n.id,
            title: n.title || 'Notification',
            subtitle: n.message || '',
            meta: formatRelative(n.createdAt || n.created_at || null),
            href: parsedData?.deepLink || undefined,
            severity: n.type === 'error' ? 'critical' : n.type === 'warning' ? 'medium' : 'info',
          };
        }),
      });
    } catch (e: any) {
      updateSection('notifications', { loading: false, error: 'Could not load notifications', items: [] });
    }
  }, [lastLoginAt]);

  const fetchAll = useCallback(() => {
    fetchMessages();
    fetchPages();
    fetchOverdue();
    fetchCapas();
    fetchNotifications();
  }, [fetchMessages, fetchPages, fetchOverdue, fetchCapas, fetchNotifications]);

  useEffect(() => {
    if (isOpen) fetchAll();
  }, [isOpen, fetchAll]);

  if (!isOpen) return null;

  const totalItems = Object.values(sections).reduce((sum, s) => sum + s.items.length, 0);
  const stillLoading = Object.values(sections).some(s => s.loading);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />

      {/* Slide-out panel */}
      <div
        className="fixed top-0 right-0 z-50 h-full w-full max-w-xl bg-white shadow-2xl flex flex-col animate-in slide-in-from-right"
        role="dialog"
        aria-label="Catch me up panel"
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-purple-600 to-purple-800 text-white px-6 py-5 flex items-start gap-3">
          <div className="p-2.5 bg-white/15 rounded-xl">
            <Clock size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold leading-tight">Catch me up</h2>
            <p className="text-xs text-purple-100 mt-0.5">
              Since your last login {lastLoginAt ? formatRelative(lastLoginAt) : '(approx. last 24 hours)'}
            </p>
          </div>
          <button
            onClick={fetchAll}
            disabled={stillLoading}
            className="p-2 rounded-lg hover:bg-white/15 transition disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw size={16} className={stillLoading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/15 transition"
            title="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Summary strip */}
        <div className="bg-purple-50 border-b border-purple-100 px-6 py-3 flex items-center gap-2 text-sm">
          {stillLoading ? (
            <>
              <Loader2 size={14} className="animate-spin text-purple-600" />
              <span className="text-purple-700">Gathering everything that happened while you were away…</span>
            </>
          ) : totalItems === 0 ? (
            <>
              <CheckCircle2 size={14} className="text-emerald-600" />
              <span className="text-emerald-700 font-medium">You're all caught up. Nothing needs your attention.</span>
            </>
          ) : (
            <>
              <AlertTriangle size={14} className="text-amber-600" />
              <span className="text-slate-700">
                <strong>{totalItems}</strong> item{totalItems === 1 ? '' : 's'} need your attention across{' '}
                <strong>{Object.values(sections).filter(s => s.items.length > 0).length}</strong> area
                {Object.values(sections).filter(s => s.items.length > 0).length === 1 ? '' : 's'}.
              </span>
            </>
          )}
        </div>

        {/* Sections */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50">
          {Object.values(sections).map(section => (
            <div key={section.key} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              {/* Section header */}
              <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3">
                <div className="p-2 bg-purple-100 text-purple-700 rounded-lg">
                  {section.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-sm text-slate-900">{section.title}</h3>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                  section.items.length > 0 ? 'bg-purple-100 text-purple-700' : 'bg-emerald-50 text-emerald-700'
                }`}>
                  {section.loading ? '…' : section.items.length}
                </span>
              </div>

              {/* Section body */}
              <div className="p-3">
                {section.loading ? (
                  <div className="px-3 py-6 flex items-center justify-center text-slate-400 text-sm">
                    <Loader2 size={14} className="animate-spin mr-2" />
                    Loading…
                  </div>
                ) : section.error ? (
                  <div className="px-3 py-4 text-xs text-slate-500 italic">{section.error}</div>
                ) : section.items.length === 0 ? (
                  <div className="px-3 py-4 flex items-center gap-2 text-xs text-emerald-700">
                    <CheckCircle2 size={14} />
                    {section.emptyText}
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {section.items.map(item => (
                      <li key={item.id}>
                        <button
                          onClick={() => {
                            if (item.href) {
                              navigate(item.href);
                              onClose();
                            }
                          }}
                          disabled={!item.href}
                          className="w-full flex items-start gap-3 px-3 py-3 text-left hover:bg-slate-50 rounded-lg transition disabled:cursor-default"
                        >
                          <span className={`mt-0.5 inline-flex items-center justify-center w-1.5 h-1.5 rounded-full ${
                            item.severity === 'critical' ? 'bg-red-500' :
                            item.severity === 'high' ? 'bg-orange-500' :
                            item.severity === 'medium' ? 'bg-amber-500' :
                            'bg-blue-500'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{item.title}</p>
                            {item.subtitle && (
                              <p className="text-xs text-slate-500 truncate mt-0.5">{item.subtitle}</p>
                            )}
                            {item.meta && (
                              <p className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-wide font-semibold">{item.meta}</p>
                            )}
                          </div>
                          {item.href && <ChevronRight size={14} className="text-slate-300 mt-1 flex-shrink-0" />}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}

          {/* Footer hint */}
          <div className="text-[11px] text-slate-400 text-center pt-2 pb-4 px-4">
            Need a deeper analysis? Open the AI assistant and ask "explain this digest".
          </div>
        </div>
      </div>
    </>
  );
};

export default CatchMeUpPanel;
